'use client';

import { ArrowRight } from 'lucide-react';

import { useCallback, useEffect, useRef, useState } from 'react';

import Image from 'next/image';
import Link from 'next/link';

import { cn } from '@/lib/utils';
import { APP_ROUTES } from '@/shared/constants/navigation';

import { AI_AGENT_SLIDES } from '../../constants/landing-constants';

const SLIDE_INTERVAL_MS = 2800;

export const AiAgentSection = () => {
	const sectionRef = useRef<HTMLElement>(null);
	const [activeIndex, setActiveIndex] = useState(0);
	const [isInView, setIsInView] = useState(false);
	const [isHovering, setIsHovering] = useState(false);

	const goToSlide = useCallback((index: number) => {
		setActiveIndex(index);
	}, []);

	const handleMouseEnter = useCallback(() => {
		setIsHovering(true);
	}, []);

	const handleMouseLeave = useCallback(() => {
		setIsHovering(false);
	}, []);

	useEffect(() => {
		const node = sectionRef.current;
		if (!node) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				setIsInView(Boolean(entry?.isIntersecting));
			},
			{ threshold: 0.45 }
		);

		observer.observe(node);
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		if (!isInView || isHovering) return;
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

		const intervalId = window.setInterval(() => {
			setActiveIndex((current) => (current + 1) % AI_AGENT_SLIDES.length);
		}, SLIDE_INTERVAL_MS);

		return () => window.clearInterval(intervalId);
	}, [isInView, isHovering, activeIndex]);

	const activeSlide = AI_AGENT_SLIDES[activeIndex];

	return (
		<section
			ref={sectionRef}
			className="landing-width mb-24 flex w-full flex-col items-center md:mb-32"
		>
			<h2 className="landing-heading text-center text-balance !text-[32px] tracking-tight md:!text-[48px]">
				Use with your preferred AI agent
			</h2>
			<p className="mt-5 max-w-3xl text-center text-pretty text-base leading-relaxed text-[hsl(215_20%_38%)] md:mt-6 md:text-[1.375rem] md:leading-8">
				Talk to your hiring data from Claude, ChatGPT, Codex, Grok or just use the
				inbuilt AI agent that comes with Binary.
			</p>
			<Link
				href={APP_ROUTES.MCP}
				className="mb-10 mt-5 inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-4 py-2 text-sm font-medium text-primary/80 transition-colors hover:bg-accent md:mb-14 md:mt-6"
			>
				See what all you can do with AI
				<ArrowRight className="size-4 shrink-0" />
			</Link>

			<div
				className="flex w-full max-w-3xl flex-col items-center gap-6"
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			>
				<div
					className="w-full overflow-hidden rounded-[min(1vw,12px)] bg-white outline-1 -outline-offset-1 outline-black/5"
					aria-roledescription="carousel"
					aria-label="AI agent examples"
				>
					<Image
						src={activeSlide.src}
						alt={activeSlide.alt}
						width={1800}
						height={1120}
						unoptimized
						sizes="(min-width: 768px) 768px, 100vw"
						className="h-auto w-full object-contain"
					/>
				</div>

				<div
					className="flex items-center gap-2"
					role="tablist"
					aria-label="Carousel slides"
				>
					{AI_AGENT_SLIDES.map((slide, index) => {
						const isActive = index === activeIndex;

						return (
							<button
								key={slide.src}
								type="button"
								role="tab"
								aria-selected={isActive}
								aria-label={slide.alt}
								onClick={() => goToSlide(index)}
								className={cn(
									'size-2.5 rounded-full transition-[background-color]',
									isActive ? 'bg-primary' : 'bg-black/15'
								)}
							/>
						);
					})}
				</div>
			</div>
		</section>
	);
};
